<template>
  <div
    class="drawable-image-root"
    :style="{ width: `${viewportWidth}px`, height: `${viewportHeight}px` }"
  >
    <ZoomableImageViewport
      ref="irZoomViewport"
      :width="viewportWidth"
      :height="viewportHeight"
      :levels="thermalZoomLevels"
      pan-ignore-selector=".rectEdge, .rectCorner, .rect-main, .g-rect, .rect-group"
      :pixelated="true"
      :clamp-bounds="true"
      @transform="onIrZoomTransform"
      @mousemove.native="displayDegree"
    >
      <v-img
        max-width="640"
        max-height="512"
        :width="width"
        :height="height"
        :src="processedImageUrl"
        class="ir-view-host"
        :class="'ir-view-host--' + irViewMode"
      >
        <canvas
          ref="heatmapCanvas"
          class="thermal-heatmap-canvas"
          :class="{ 'thermal-heatmap-canvas--hidden': irViewMode === 'image' }"
        />
        <div id="canvas" class="ir-defect-canvas" width="100%" height="100%" />
      </v-img>

      <!-- 화면 고정: 활성/비활성 박스 테두리 + 온도 HUD -->
      <template #overlay="{ imageToScreen }">
        <div
          v-for="(box, idx) in inactiveDefectRects"
          :key="'inactive-defect-' + idx"
          class="inactive-defect-outline"
          :style="defectRectOutlineStyle(imageToScreen, box)"
        />
        <div
          v-show="hasThermoRect"
          class="thermo-rect-outline"
          :style="thermoRectOutlineStyle(imageToScreen)"
        />
        <div
          v-show="thermoHud.visible"
          ref="thermoHudEl"
          class="thermo-hud pa-1 rounded"
          :style="thermoHudScreenStyle(imageToScreen)"
        >
          <div class="text-caption thermo-hud__line">Max: {{ formatThermo(thermoHud.max) }}</div>
          <div class="text-caption thermo-hud__line">Min: {{ formatThermo(thermoHud.min) }}</div>
          <div class="text-caption thermo-hud__line">Avg: {{ formatThermo(thermoHud.avr) }}</div>
        </div>
      </template>
    </ZoomableImageViewport>

    <div
      v-if="showThermalHeatmapTools"
      class="drawable-image-toolbar d-flex align-center"
    >
      <v-btn-toggle
        v-model="irViewMode"
        dense
        mandatory
        class="ir-view-mode-toggle"
      >
        <v-btn x-small value="image" class="text-none px-2">
          {{ $t('defects.irViewImage') }}
        </v-btn>
        <v-btn x-small value="overlay" class="text-none px-2">
          {{ $t('defects.irViewOverlay') }}
        </v-btn>
        <v-btn x-small value="heatmap" class="text-none px-2">
          {{ $t('defects.irViewHeatmap') }}
        </v-btn>
      </v-btn-toggle>
      <span v-if="heatmapStats && irViewMode !== 'image'" class="text-caption text--secondary ml-2">
        {{ $t('defects.heatmapRelativeScale') }}
        {{ heatmapStats.min }}~{{ heatmapStats.max }}°C
      </span>
      <v-btn
        v-if="canDownloadHeatmap"
        x-small
        text
        class="text-none ml-1 px-2"
        :loading="heatmapDownloading"
        :disabled="heatmapDownloading"
        @click="downloadHeatmapPng"
      >
        {{ $t('defects.downloadHeatmap') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import DefectDrawer from '../../modules/dot-d3/DefectDrawer';
import CoordUtils from '../../modules/solar/CoordUtils';
import ThermalDataHolder from '../../modules/thermal/ThermalDataHolder';
import { optimizeCanvasPerformanceVue } from '@/utils/canvasOptimizer';
import { processImageUrlForCdn, isLocalDevHost } from '@/utils/imagePolygonManager/imageLoader';
import { resolveDefectShapeGeomForOverlay } from '@/utils/defect/defectShapeOverlay';
import { THERMAL_ZOOM_LEVELS, placeOverlayBesideRect } from '@/lib/zoomable-viewer';
import {
  downloadThermalHeatmapPng,
  renderThermalHeatmapToCanvas
} from '@/lib/thermal-heatmap';
import ZoomableImageViewport from '@/components/image/ZoomableImageViewport.vue';

/** IR 뷰포트 표시 크기 (RGB ImageZoomViewer height=512 와 맞춤) */
const IR_VIEWPORT_WIDTH = 640;
const IR_VIEWPORT_HEIGHT = 512;

export default {
  components: {
    ZoomableImageViewport
  },
  props: {
    thermalImage: {
      default: null,
      type: Object
    },
    defectInfo: {
      default: null,
      type: Object
    }
  },
  data: () => ({
    width: 640,
    height: 512,
    viewportWidth: IR_VIEWPORT_WIDTH,
    viewportHeight: IR_VIEWPORT_HEIGHT,
    thermalZoomLevels: [...THERMAL_ZOOM_LEVELS],
    canvas: undefined,
    markerCoords: null,
    defectCoords: undefined,
    ratesOfM: null,
    ratesOfCm: null,
    distancePerPixel: null,
    defaultImage: require('../../assets/default.png'),
    UTMKCoordinates: null,
    defectDrawer: null,
    thermalDataHolder: new ThermalDataHolder(),
    irZoom: { x: 0, y: 0, k: 1, levelIndex: 0 },
    thermoHud: {
      visible: false,
      imageX: 0,
      imageY: 0,
      rect: { x: 0, y: 0, w: 0, h: 0 },
      max: '-',
      min: '-',
      avr: '-'
    },
    thermoHudSize: { w: 96, h: 56 },
    /** 다른 결함 박스 (화면 고정 테두리용, 이미지 픽셀 좌표) */
    inactiveDefectRects: [],
    /** image | overlay | heatmap — 혼합/히트맵은 로컬에서만 */
    irViewMode: 'image',
    heatmapStats: null,
    heatmapDownloading: false
  }),
  computed: {
    domainName: {
      get() {
        return process.env.VUE_APP_CDN_URL ? process.env.VUE_APP_CDN_URL : '';
      }
    },
    // CDN 프록시로 처리된 이미지 URL
    processedImageUrl() {
      if (!this.thermalImage || !this.thermalImage.imgUrl) {
        return this.defaultImage;
      }
      return processImageUrlForCdn(this.thermalImage.imgUrl);
    },
    // 사각형 위치와 관련된 필드들만 추출
    rectRelatedDefectInfo() {
      const defect = this.defectInfo;
      return {
        id: defect?.id,
        defectShapeGeom: defect?.defectShapeGeom
      };
    },
    reportId() {
      return this.$store.getters['explorer/getReportId'];
    },
    defectMode() {
      return this.$store.getters['defect/getDefectMode'];
    },
    hasThermoRect() {
      const r = this.thermoHud?.rect;
      return !!(r && Number(r.w) > 0 && Number(r.h) > 0);
    },
    /** CDN 프록시와 동일: localhost / 127.0.0.1 에서만 혼합·히트맵·저장 */
    showThermalHeatmapTools() {
      return isLocalDevHost();
    },
    canDownloadHeatmap() {
      return !!(
        this.showThermalHeatmapTools &&
        this.heatmapStats &&
        this.thermalDataHolder &&
        this.thermalDataHolder.isLoadingCompleted()
      );
    }
  },

  watch: {
    thermalImage: {
      deep: true,
      immediate: true,
      handler(newThermalImage) {
        console.log(
          '[DRAW]thermal image changed  ',
          JSON.parse(JSON.stringify(newThermalImage))
        );
        if (!newThermalImage || !this.defectDrawer) return;
        if (newThermalImage.imgPosCoords) {
          this.handleImageChange(newThermalImage);
        }
      }
    },
    // 사각형과 관련된 필드들만 watch
    rectRelatedDefectInfo: {
      deep: true,
      immediate: true,
      handler(newRectInfo, oldRectInfo) {
        if (this.defectDrawer) {
          console.log('[RECT_DRAG_TEST] 사각형 관련 정보 변경됨:', { new: newRectInfo, old: oldRectInfo });
          this.handleDefectChange(this.defectInfo);
        }
      }
    },
    irViewMode() {
      this.$nextTick(() => this.renderThermalHeatmap());
    }
  },

  mounted() {
    this.defectDrawer = new DefectDrawer({
      addTo: '#canvas',
      thermo_fn: this.handleMovingDefectBox,
      addBorderRect: false,
      debug: false,
      store: this.$store,
      screenSpaceThermoHud: true,
      onThermoHudUpdate: this.onThermoHudUpdate
    });

    // 🆕 Canvas 성능 최적화 설정
    optimizeCanvasPerformanceVue(this, 'canvas', 'DrawableImage');

    if (this.$props.thermalImage && Object.entries(this.$props.thermalImage).length !== 0) {
      if (
        this.$props.thermalImage.imgPosCoords &&
        this.$props.thermalImage.imgPosCoords?.coordinates[0]
      ) {
        this.initUTMKCoordinates(this.$props.thermalImage);
      }
      this.handleImageChange(this.$props.thermalImage);
    }

    if (this.$props.defectInfo) {
      this.handleDefectChange(this.$props.defectInfo);
    }
  },

  beforeUnmount() {
    // 이벤트 리스너 정리
    if (this.$eventBus) {
      this.$eventBus.$off('d3-event');
      this.$eventBus.$off('defect-temperature');
    }

    // thermalDataHolder 메모리 정리
    if (this.thermalDataHolder) {
      this.thermalDataHolder.clearData();
    }

    // defectDrawer 정리
    if (this.defectDrawer) {
      this.defectDrawer = null;
    }

  },

  methods: {
    init: function () {},

    resetIrZoom() {
      this.$refs.irZoomViewport?.reset?.();
    },

    getIrZoomTransform() {
      return this.$refs.irZoomViewport?.getTransform?.() ?? { x: 0, y: 0, k: 1, levelIndex: 0 };
    },

    /** 줌 변환만 갱신 — 라벨은 HUD라 스케일 상쇄 불필요 */
    onIrZoomTransform(transform) {
      this.irZoom = {
        x: transform?.x ?? 0,
        y: transform?.y ?? 0,
        k: transform?.k ?? 1,
        levelIndex: transform?.levelIndex ?? 0
      };
    },

    onThermoHudUpdate(payload) {
      if (!payload) {
        this.thermoHud.visible = false;
        return;
      }
      const rect = payload.rect || {};
      this.thermoHud = {
        visible: payload.visible !== false,
        imageX: Number(payload.imageX) || 0,
        imageY: Number(payload.imageY) || 0,
        rect: {
          x: Number(rect.x) || 0,
          y: Number(rect.y) || 0,
          w: Number(rect.w) || 0,
          h: Number(rect.h) || 0
        },
        max: payload.max,
        min: payload.min,
        avr: payload.avr
      };
      this.$nextTick(() => this.measureThermoHud());
    },

    measureThermoHud() {
      const el = this.$refs.thermoHudEl;
      if (!el || !this.thermoHud.visible) {
        return;
      }
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (w > 0 && h > 0) {
        this.thermoHudSize = { w, h };
      }
    },

    resolveImageToScreen(imageToScreenFn) {
      if (typeof imageToScreenFn === 'function') {
        return imageToScreenFn;
      }
      return (x, y) => ({
        x: this.irZoom.x + this.irZoom.k * x,
        y: this.irZoom.y + this.irZoom.k * y
      });
    },

    /** 화면 픽셀 고정 테두리 — 확대해도 두께·선명도 유지 */
    thermoRectOutlineStyle(imageToScreenFn) {
      return this.defectRectOutlineStyle(imageToScreenFn, this.thermoHud.rect);
    },

    defectRectOutlineStyle(imageToScreenFn, box) {
      const map = this.resolveImageToScreen(imageToScreenFn);
      const r = box || {};
      const topLeft = map(r.x || 0, r.y || 0);
      const botRight = map((r.x || 0) + (r.w || 0), (r.y || 0) + (r.h || 0));
      return {
        left: `${topLeft.x}px`,
        top: `${topLeft.y}px`,
        width: `${Math.max(0, botRight.x - topLeft.x)}px`,
        height: `${Math.max(0, botRight.y - topLeft.y)}px`
      };
    },

    thermoHudScreenStyle(imageToScreenFn) {
      const map = this.resolveImageToScreen(imageToScreenFn);

      const r = this.thermoHud.rect || {};
      const topLeft = map(r.x || 0, r.y || 0);
      const botRight = map((r.x || 0) + (r.w || 0), (r.y || 0) + (r.h || 0));
      const placed = placeOverlayBesideRect({
        rectScreen: {
          x: topLeft.x,
          y: topLeft.y,
          w: Math.max(0, botRight.x - topLeft.x),
          h: Math.max(0, botRight.y - topLeft.y)
        },
        overlaySize: this.thermoHudSize,
        viewport: { w: this.viewportWidth, h: this.viewportHeight },
        gap: 8
      });

      return {
        left: `${placed.left}px`,
        top: `${placed.top}px`
      };
    },

    formatThermo(value) {
      if (value == null || value === '' || value === '-') {
        return '-';
      }
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return `${value}°C`;
      }
      return `${n}°C`;
    },

    handleMovingDefectBox: function (d) {
      // SVG 좌표를 온도 그리드 인덱스로 변환 (이미지 해상도 ≠ 온도 그리드 크기일 수 있음)
      const svgW = this.defectDrawer ? this.defectDrawer.options.w : 640;
      const svgH = this.defectDrawer ? this.defectDrawer.options.h : 512;
      const gridW = this.thermalDataHolder.mapPixelWidth || svgW;
      const gridH = this.thermalDataHolder.mapPixelHeight || svgH;
      const scaleX = gridW / Math.max(1, svgW);
      const scaleY = gridH / Math.max(1, svgH);

      const rectHeat = this.thermalDataHolder.getMinMaxAverage(
        Math.floor(d.x * scaleX),
        Math.floor(d.y * scaleY),
        Math.floor((d.x + d.w) * scaleX),
        Math.floor((d.y + d.h) * scaleY)
      );

      // 결함정보 대화상자의 결함온도 min/max/avr 값을 제공하기 위해
      // event를 발송한다.
      this.$eventBus.$emit('defect-temperature', rectHeat);

      this.handleDrawingEvent(d);

      return rectHeat;
    },

    handleImageChange(newThermalImage) {
      // reportId가 변경되었는지 체크
      const currentReportId = this.reportId;
      const newReportId = newThermalImage?.solarExamineReport;

      if (currentReportId !== newReportId) {
        console.log('[DRAWABLE IMAGE] reportId 변경됨:', { currentReportId, newReportId });
        // reportId가 변경되면 초기 사각형으로 리셋
        this.defectDrawer.resetToDefaultRect();
      }

      const iw = Math.max(1, Number(newThermalImage.imgWidth) || 640);
      const ih = Math.max(1, Number(newThermalImage.imgHeight) || 512);
      this.defectDrawer.reset({ w: iw, h: ih });

      this.initUTMKCoordinates(newThermalImage);
      this.resetIrZoom();

      if (newThermalImage?.id) {
        this.thermalDataHolder
          .load(newThermalImage.id, {
            imgWidth: iw,
            imgHeight: ih
          })
          .then(() => {
            // result 값은 min/max/average 이므로 이 값은 사용하지 않는다.
            this.renderThermalHeatmap();
            this.handleDefectChange(this.defectInfo);
          })
          .catch((error) => {
            console.error('[DRAWABLE IMAGE] thermal data load failed', error);
            this.heatmapStats = null;
            this.handleDefectChange(this.defectInfo);
          });
      } else {
        this.heatmapStats = null;
      }
    },

    renderThermalHeatmap() {
      if (!this.showThermalHeatmapTools || this.irViewMode === 'image') {
        this.heatmapStats = null;
        return;
      }
      const canvas = this.$refs.heatmapCanvas;
      const holder = this.thermalDataHolder;
      if (!canvas || !holder || !holder.isLoadingCompleted()) {
        console.warn('[THERMAL HEATMAP] skip render', {
          hasCanvas: !!canvas,
          hasHolder: !!holder,
          loadCompleted: holder?.isLoadingCompleted?.()
        });
        this.heatmapStats = null;
        return;
      }

      this.logThermalDataDiagnostics(holder);

      const result = renderThermalHeatmapToCanvas(canvas, holder.dataMap, {
        // 절대 °C 고정 구간이 아니라, 이 장 격자 min~max 상대 스케일
        scaleMode: 'relative',
        // 히트맵만 볼 때는 불투명, 혼합일 때는 반투명
        alpha: this.irViewMode === 'heatmap' ? 255 : 170
      });
      if (result) {
        this.heatmapStats = {
          min: Number(result.min).toFixed(1),
          max: Number(result.max).toFixed(1),
          scaleMode: result.scaleMode || 'relative'
        };
        console.log('[THERMAL HEATMAP] canvas render ok', {
          canvasCss: {
            width: canvas.style.width,
            height: canvas.style.height,
            clientW: canvas.clientWidth,
            clientH: canvas.clientHeight
          },
          canvasBuffer: { width: canvas.width, height: canvas.height },
          relativeScale: { min: result.min, max: result.max, mode: result.scaleMode },
          irViewMode: this.irViewMode,
          alpha: this.irViewMode === 'heatmap' ? 255 : 170
        });
      } else {
        this.heatmapStats = null;
        console.error('[THERMAL HEATMAP] canvas render failed (null result)');
      }
    },

    async downloadHeatmapPng() {
      if (!this.showThermalHeatmapTools) {
        return;
      }
      const holder = this.thermalDataHolder;
      if (!holder || !holder.isLoadingCompleted() || this.heatmapDownloading) {
        return;
      }
      this.heatmapDownloading = true;
      try {
        const id = this.thermalImage?.id ?? 'thermal';
        const fileName = `heatmap-${id}-${holder.mapPixelWidth}x${holder.mapPixelHeight}.png`;
        await downloadThermalHeatmapPng(holder.dataMap, {
          alpha: 255,
          fileName
        });
      } catch (error) {
        console.error('[THERMAL HEATMAP] download failed', error);
      } finally {
        this.heatmapDownloading = false;
      }
    },

    /**
     * 히트맵이 이상할 때 원인 파악용 — 온도 그리드 구조/샘플/IR 크기 비교
     */
    logThermalDataDiagnostics(holder) {
      const dataMap = holder?.dataMap;
      if (!Array.isArray(dataMap) || dataMap.length === 0) {
        console.warn('[THERMAL DATA DIAG] dataMap empty', { holder });
        return;
      }

      const height = dataMap.length;
      const rowLens = dataMap.slice(0, 20).map((row) => (Array.isArray(row) ? row.length : -1));
      const width0 = Array.isArray(dataMap[0]) ? dataMap[0].length : 0;
      let jagged = false;
      let nanCount = 0;
      let finiteCount = 0;
      let minScan = Infinity;
      let maxScan = -Infinity;
      let sum = 0;

      for (let y = 0; y < height; y += 1) {
        const row = dataMap[y];
        if (!Array.isArray(row)) {
          jagged = true;
          continue;
        }
        if (row.length !== width0) {
          jagged = true;
        }
        for (let x = 0; x < row.length; x += 1) {
          const v = row[x];
          if (!Number.isFinite(v)) {
            nanCount += 1;
            continue;
          }
          finiteCount += 1;
          sum += v;
          if (v < minScan) minScan = v;
          if (v > maxScan) maxScan = v;
        }
      }

      const midY = Math.floor(height / 2);
      const midX = Math.floor(width0 / 2);
      const sampleRows = [0, midY, height - 1]
        .filter((y, i, arr) => y >= 0 && y < height && arr.indexOf(y) === i)
        .map((y) => {
          const row = dataMap[y] || [];
          const xs = [0, midX, Math.max(0, row.length - 1)];
          return {
            y,
            rowLen: row.length,
            cornersAndCenter: xs.map((x) => ({ x, v: row[x] })),
            first8: row.slice(0, 8)
          };
        });

      const irW = Number(this.width) || Number(this.thermalImage?.imgWidth) || null;
      const irH = Number(this.height) || Number(this.thermalImage?.imgHeight) || null;

      console.log('[THERMAL DATA DIAG] summary', {
        thermalImageId: this.thermalImage?.id ?? holder.currentThermalImageId,
        grid: {
          height,
          widthRow0: width0,
          mapPixelWidth: holder.mapPixelWidth,
          mapPixelHeight: holder.mapPixelHeight,
          jaggedRows: jagged,
          first20RowLengths: rowLens
        },
        holderMinMax: { min: holder.minValue, max: holder.maxValue, average: holder.average },
        scannedMinMax: {
          min: minScan,
          max: maxScan,
          average: finiteCount ? sum / finiteCount : null,
          finiteCount,
          nanCount
        },
        irImageSize: { width: irW, height: irH },
        sizeMismatch:
          irW && irH && width0 && height
            ? { dW: width0 - irW, dH: height - irH, ratioW: width0 / irW, ratioH: height / irH }
            : null,
        samples: sampleRows
      });
    },

    async handleDefectChange(newDefectInfo) {

      const overlayGeom = resolveDefectShapeGeomForOverlay(newDefectInfo);
      if (newDefectInfo?.id && overlayGeom?.coordinates) {
        console.log('[RECT_DRAG_TEST] 실제 결함 사각형 정보 있음, 적용');
        const rectDim = this.convertCoordsToRectDims(
          overlayGeom.type,
          overlayGeom.coordinates
        );
        console.log('[RECT_DRAG_TEST] 변환된 rectDim:', rectDim);

        const options = {
          x: rectDim.x,
          y: rectDim.y,
          rect_w: rectDim.w,
          rect_h: rectDim.h
        };
        this.defectDrawer.reset(options);
      } else if (!newDefectInfo?.id) {
        // defect ID가 없는 경우 (새 결함 생성 모드)에만 저장된 사각형 로드
        console.log('[RECT_DRAG_TEST] 새 결함 생성 모드 - 저장된 기본 사각형 로드 시도');

        if (this.defectDrawer !== null) {
          // 저장된 기본 사각형 정보 가져오기
          const savedRect = this.$store.getters['defect/getCurrentReportDefaultRect'];
          console.log('[RECT_DRAG_TEST] 저장된 기본 사각형:', savedRect);

          if (savedRect && savedRect.reportId) {
            // 저장된 사각형 정보로 options 구성
            const options = {
              x: savedRect.x,
              y: savedRect.y,
              rect_w: savedRect.width,
              rect_h: savedRect.height
            };
            console.log('[RECT_DRAG_TEST] 저장된 사각형으로 options 구성:', options);
            this.defectDrawer.reset(options);
          } else {
            // 저장된 사각형이 없으면 기본 사각형 표시
            console.log('[RECT_DRAG_TEST] 저장된 사각형 없음, 기본 사각형으로 표시');
            this.defectDrawer.reset({});
          }
        }
      } else {
        // defect ID가 있지만 defectShapeGeom이 없는 경우 (결함 유형 변경 등)
        // 사각형 위치는 그대로 유지하고 변경하지 않음
        console.log('[RECT_DRAG_TEST] 결함 유형 변경 등 - 사각형 위치 그대로 유지');
      }

      //inactive한 사각형 처리
      if (this.thermalImage && this.thermalImage.defectCount > 0) {
        const defectCoordsArr = new Array();
        if (newDefectInfo) {
          this.thermalImage.moduleDefects
            .filter((defect) => defect.id !== newDefectInfo.id)
            .forEach((defect) => {
              const geom = resolveDefectShapeGeomForOverlay(defect);
              if (!geom?.coordinates) return;
              defectCoordsArr.push(
                this.convertCoordsToRectDims(geom.type, geom.coordinates)
              );
            });
        } else {
          this.thermalImage.moduleDefects.forEach((defect) => {
            const geom = resolveDefectShapeGeomForOverlay(defect);
            if (!geom?.coordinates) return;
            defectCoordsArr.push(
              this.convertCoordsToRectDims(geom.type, geom.coordinates)
            );
          });
        }

        this.inactiveDefectRects = defectCoordsArr;
        this.defectDrawer.makeInactiveRect(defectCoordsArr);
      } else {
        this.inactiveDefectRects = [];
        this.defectDrawer.makeInactiveRect([]);
      }
      //모달유형별 사각형처리가 추가됨
      //수정 ,추가 일 경우에만 결함 사각형정보를 저장
    },

    convertCoordsToRectDims: function (shapeType, shapeCoords) {
      console.log('convert to coords to rect dims', shapeCoords);
      let minX = 640,
          minY = 512,
          maxX = 0,
          maxY = 0;

      let shapeCoords1 = shapeCoords;
      if (shapeType === 'Polygon') {
        shapeCoords1 = shapeCoords[0];
      }
      shapeCoords1.forEach((coords) => {
        if (coords[0] > maxX) {
          maxX = coords[0];
        }

        if (coords[0] < minX) {
          minX = coords[0];
        }

        if (coords[1] > maxY) {
          maxY = coords[1];
        }

        if (coords[1] < minY) {
          minY = coords[1];
        }
      });

      return {
        x: minX,
        y: minY,
        w: maxX - minX,
        h: maxY - minY
      };
    },

    handleDrawingEvent: function (d) {
      if (!this.UTMKCoordinates || !this.distancePerPixel) {
        console.warn('[DRAWABLE IMAGE] UTMKCoordinates 또는 distancePerPixel이 초기화되지 않음');
        return;
      }

      // 이미지에 그려진 사각형의 각 꼭지점 값
      const coordsOnScreen = [];

      coordsOnScreen.push([d.x, d.y]);
      coordsOnScreen.push([d.x + d.w, d.y]);
      coordsOnScreen.push([d.x + d.w, d.y + d.h]);
      coordsOnScreen.push([d.x, d.y + d.h]);

      this.markerCoords = {
        left_x: d.x,
        top_y: d.y,
        width: d.w,
        height: d.h
      };

      /* 결함 좌표의 계산 로직이 서버에서 수행되고 있고 다음의 코드로는 위도와 경도의 교차점 각도가 90도가 아니어서, 
      /* 지도상에 이미지가 평행사변형으로 배치되는 경우에는 현재의 계산 로직으로는 결함의 중점이 이미지의 외곽 경계선을 벗어나는 경우가 있고,
      /* 또한 지도상에 위치 오차가 발생하여 프론트엔드에서 결함의 중심 좌표점을 계산하는 로직을 주석 처리하고 서버에 있는 로직을 사용하도록 함.
      /* 또한 동일한 결과를 산출하는 로직을 프론트와 서버에 각각 두는 것은 유지보수관점에서도 좋지 않아서 계산을 서버로 이전함.
      /* 2026. 3. 18 수정 Yeongjong Kang. */
      // 중심 좌표를 확인하는 공식
      //사각형의 중심좌표를 찾아 계산식에 활용한다.
      // const centerCoords = {
      //   x: d.x + d.w / 2,
      //   y: d.y + d.h / 2
      // };

      // (주의) X 값은 +를 해야 하고, Y 값은 -를 해야 한다.
      // const utmMarkerCoords = [
      //   this.UTMKCoordinates.startPoint.x +
      //     centerCoords.x * this.distancePerPixel,
      //   this.UTMKCoordinates.startPoint.y -
      //     centerCoords.y * this.distancePerPixel
      // ];

      // const wgsCoords = CoordUtils.transUTMKToWGS84(utmMarkerCoords);

      // this.defectCoords = {
      //   type: 'Point',
      //   crs: {
      //     type: 'name',
      //     properties: {
      //       name: 'EPSG:4326'
      //     }
      //   },
      //   coordinates: wgsCoords
      // };
    },
    initUTMKCoordinates(image) {
      const xy1 = CoordUtils.transWGS84ToUTMK(
        image.imgPosCoords?.coordinates[0][0]
      );
      const xy2 = CoordUtils.transWGS84ToUTMK(
        image.imgPosCoords?.coordinates[0][2]
      );

      const coordinates = {
        startPoint: { x: xy1[0], y: xy1[1] }, // xy1[0] = 시작점 x, xy1[1] = 시작점 y
        x: Math.abs(xy2[0] - xy1[0]),
        y: Math.abs(xy2[1] - xy1[1])
      };

      this.width = image.imgWidth;
      this.height = image.imgHeight;
      this.UTMKCoordinates = coordinates;
      this.distancePerPixel = coordinates.x / image.imgWidth;
    },
    displayDegree(e) {
      if (this.thermalPositionMap) {
        this.pointThermalValue = this.thermalPositionMap[e.offsetY][e.offsetX];
        // console.log("thermal degree on point", this.pointThermalValue);
      }
    },

    // 사각형 위치 로드 (store에서 값을 가져와서 DefectDrawer에 적용)
    async loadRectangleTemplate() {

      if (!this.defectDrawer) {
        console.log('[DRAWABLE IMAGE] ❌ defectDrawer가 없어서 로드 불가');
        return false;
      }

      try {
        // store에서 저장된 사각형 위치 가져오기
        const savedRect = this.$store.getters['defect/getCurrentReportDefaultRect'];
        console.log('[DRAWABLE IMAGE] 저장된 사각형 위치:', savedRect);

        if (savedRect && savedRect.reportId) {
          // reset을 사용하여 사각형 위치 설정
          const options = {
            x: savedRect.x,
            y: savedRect.y,
            rect_w: savedRect.width,
            rect_h: savedRect.height
          };
          this.defectDrawer.reset(options);

          return true;
        } else {
          console.log('[DRAWABLE IMAGE] 저장된 사각형 위치 없음, 기본값 사용');
          return false;
        }
      } catch (error) {
        console.error('[DRAWABLE IMAGE] 사각형 위치 로드 실패:', error);
        return false;
      }
    }
  }
};
</script>

<style>
.drawable-image-root {
  position: relative;
  max-width: 100%;
  overflow: hidden;
}

.drawable-image-toolbar {
  position: absolute;
  left: 8px;
  bottom: 8px;
  z-index: 3;
  pointer-events: auto;
  padding: 2px 4px;
  border-radius: 4px;
  background-color: rgba(15, 23, 42, 0.45);
}

.thermal-heatmap-canvas {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  image-rendering: pixelated;
  z-index: 0;
}

.thermal-heatmap-canvas--hidden {
  opacity: 0;
}

/* 히트맵만: IR 이미지 숨기고 어두운 바탕 */
.ir-view-host--heatmap {
  background-color: #0b1220;
}

.ir-view-host--heatmap >>> .v-image__image,
.ir-view-host--heatmap ::v-deep .v-image__image {
  opacity: 0 !important;
}

.ir-view-mode-toggle {
  background-color: transparent;
}

.thermo-hud {
  position: absolute;
  z-index: 2;
  pointer-events: none;
  background-color: rgba(15, 23, 42, 0.28);
  border: 1px solid rgba(248, 250, 252, 0.18);
  color: #f8fafc;
  line-height: 1.25;
  white-space: nowrap;
  transform: translateY(-2px);
}

.thermo-hud__line {
  font-weight: 500;
  letter-spacing: 0.01em;
}

/* 화면 고정 테두리: 확대와 무관하게 얇고 선명 */
.thermo-rect-outline {
  position: absolute;
  z-index: 1;
  pointer-events: none;
  box-sizing: border-box;
  border: 1.5px solid #c8ff7a;
  box-shadow: 0 0 0 1px #000000;
}

.inactive-defect-outline {
  position: absolute;
  z-index: 1;
  pointer-events: none;
  box-sizing: border-box;
  border: 1.5px solid rgba(0, 220, 255, 0.92);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.9);
  opacity: 0.85;
}

.ir-defect-canvas {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
}

/*
 * 줌 레이어 안 rect: fill만 (드래그 hit).
 * stroke는 overlay 가 담당 → 확대 시 굵어지지 않음.
 */
rect.rect-main {
  fill: rgba(57, 255, 20, 0.04);
  fill-opacity: 1;
  stroke: none;
  pointer-events: all;
  cursor: move;
}

g.active rect.rect-main {
  fill: rgba(57, 255, 20, 0.06);
  stroke: none;
}

rect.rect-sub.inactive {
  fill: none;
  stroke: none;
}

rect.rectEdge {
  fill: var(--v-primary-base);
  opacity: 0;
}

rect.rectEdge:hover {
  cursor: move;
}

rect.rectCorner {
  fill: var(--v-success-base);
  opacity: 0;
}

rect.rectCorner.nwse:hover {
  cursor: nwse-resize;
}

rect.rectCorner.nesw:hover {
  cursor: nesw-resize;
}

rect.cornerEdge.debug {
  opacity: 0.25;
}
</style>
